import { Badge } from 'framework7-react';
import { FC, useMemo, useState } from 'react';

import { orderBy } from 'lodash';

import {
  Entertainer,
  EntertainerFilter,
  useFilteredEntertainerIdsQuery,
  useHydratedEntertainersQuery,
} from '@queries/EntertainerQueries';
import { useCurrentUserAppDashboard } from '@queries/UserQueries';

import { useCreateEntertainerReview } from '@mutations/EntertainerMutations';

import { useCurrentUserCity } from '@hooks/useCurrentUserLocation';
import { useDebounce } from '@hooks/useDebounce';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { LazyLoad } from '@components/layout/LazyLoad';
import { Popup } from '@components/layout/Popup/Popup';
import { ProgressCard } from '@components/layout/ProgressCard/ProgressCard';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { EntertainerSpotlightSection } from '@screens/series/SeriesFeatureFormScreen/SeriesFeatureFormScreen';

import {
  MostSupportedEntertainersGrid,
  SupportEntertainerCard,
} from '../DragKingCommunityScreen/DragKingThingShoutoutsScreen';
import { EntertainerRewiewsPopup } from '../EntertainerDetailsScreen/EntertainerReviews';

export const useCurrentUserSupportedEntertainersCount = (cityId: string) => {
  const currentGoal = 3;
  const { appDashboard } = useCurrentUserAppDashboard({ cityId });

  const supportEntertainersDashboardTile = appDashboard?.primaryTiles?.weeklyMetrics?.find((tile) =>
    tile?.cta?.to?.startsWith('/entertainers/support'),
  );

  const { currentUserCount = 0 } = supportEntertainersDashboardTile ?? {};
  const badgeCount = Math.max(0, currentGoal - currentUserCount);

  return { supportEntertainersDashboardTile, currentGoal, badgeCount, currentUserCount };
};

const SupportEntertainersProgressCard = ({
  cityId,
  onSupportClick,
}: {
  cityId: string;
  onSupportClick: () => void;
}) => {
  const { supportEntertainersDashboardTile, currentGoal, badgeCount, currentUserCount } =
    useCurrentUserSupportedEntertainersCount(cityId);

  const contentMessage = useMemo(() => {
    if (!currentUserCount) {
      return `You haven't supported any performers recently! Write something nice to a performer and let them know you appreciate their work.`;
    }

    if (currentUserCount === currentGoal) {
      return `You've reached the goal and have supported ${currentUserCount} performers in the area! Keep it up!`;
    }

    return `You've supported ${currentUserCount} performers in the area! Keep it up to reach the goal!`;
  }, [currentUserCount]);

  if (!supportEntertainersDashboardTile) {
    return <></>;
  }

  return (
    <ProgressCard
      milestones={[currentGoal]}
      currentValue={supportEntertainersDashboardTile.currentUserCount}
      progressLabel={
        <>
          <strong>{`${currentUserCount} supported`}</strong> of {currentGoal} performer goal
        </>
      }
      content={<div>{contentMessage}</div>}
      footer={
        <Button
          roundness="rounded"
          variant="smooth"
          color="neutral"
          onClick={onSupportClick}
          chip={badgeCount ? { text: `${badgeCount} more` } : undefined}
        >
          Support a performer
        </Button>
      }
    />
  );
};

const SupportEntertainersPopupContent: FC<{
  cityId: string;
  entertainers: Entertainer[];
}> = ({ cityId, entertainers }) => {
  const [spotlights, setSpotlights] = useState<
    { entertainerId: string; entertainerTitle?: string; reason: string }[]
  >([
    {
      entertainerId: '',
      reason: '',
    },
  ]);

  const { isSubmitting: isSubmittingReview, createEntertainerReviewAsync } =
    useCreateEntertainerReview(spotlights?.[0]?.entertainerId);
  const { refetchAppDashboard } = useCurrentUserAppDashboard({ cityId });

  const handleSubmit = async () => {
    await createEntertainerReviewAsync({
      text: spotlights?.[0]?.reason,
      meta: {
        source: 'support-entertainers-screen',
      },
    });
    await refetchAppDashboard();
  };

  const entertainerOptions = useMemo(() => {
    return entertainers.map((entertainer) => ({
      value: entertainer._id,
      label: entertainer.title,
    }));
  }, [entertainers]);

  const updateSpotlightEntertainer =
    (index: number) =>
    (
      updatedEntertainer:
        | { entertainerId: string; entertainerTitle?: undefined }
        | { entertainerId?: undefined; entertainerTitle: string },
    ) => {
      setSpotlights((prevValue) => {
        const updatedValue = [...prevValue];
        if (updatedEntertainer.entertainerId) {
          updatedValue[index].entertainerId = updatedEntertainer.entertainerId;
          updatedValue[index].entertainerTitle = undefined;
        } else {
          updatedValue[index].entertainerId = undefined;
          updatedValue[index].entertainerTitle = updatedEntertainer.entertainerTitle;
        }
        return updatedValue;
      });
    };

  const updateSpotlightEntertainerReason = (index: number) => (updatedReason: string) => {
    setSpotlights((prevValue) => {
      const updatedValue = [...prevValue];
      updatedValue[index].reason = updatedReason;
      return updatedValue;
    });
  };

  return (
    <>
      <EntertainerSpotlightSection
        isCustomInputEnabled={false}
        label="Performer to support *"
        entertainerOptions={entertainerOptions}
        spotlights={spotlights}
        index={0}
        name="spotlightOne"
        updateSpotlightEntertainer={updateSpotlightEntertainer}
        updateSpotlightEntertainerReason={updateSpotlightEntertainerReason}
      />
      <div className="mt-2">
        <Button
          disabled={
            isSubmittingReview || !spotlights?.[0]?.entertainerId || !spotlights?.[0]?.reason
          }
          roundness="rounded"
          onClick={handleSubmit}
        >
          {isSubmittingReview ? 'Sharing...' : 'Share support'}
        </Button>
      </div>
    </>
  );
};

const SupportEntertainersPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  cityId: string;
  entertainers: Entertainer[];
}> = ({ isVisible, onClose, cityId, entertainers }) => {
  return (
    <Popup isVisible={isVisible} onClose={onClose}>
      <Popup.Content>
        <TitleToolbar text="Support a Performer" size="md" />
        <div className="mb-2">
          Supporting a performer is easy! Just select the performer you want to support and write a
          short message to let the community know you appreciate their work.
        </div>
        {isVisible && (
          <SupportEntertainersPopupContent cityId={cityId} entertainers={entertainers} />
        )}
      </Popup.Content>
    </Popup>
  );
};

interface SupportEntertainersScreenProps {}

interface SupportEntertainersScreenQueryParams {
  filter?: EntertainerFilter;
  cityId?: string;
  entertainerId?: string;
}

export const SupportEntertainersScreen = createScreenComponent<
  SupportEntertainersScreenProps,
  SupportEntertainersScreenQueryParams
>(({ f7route }) => {
  const { filter = EntertainerFilter.All, cityId: cityIdOverride, entertainerId } = f7route.query;
  const [selectedEntertainerId, setSelectedEntertainerId] = useState<string>(entertainerId);
  const [isSupportEntertainersPopupVisible, setIsSupportEntertainersPopupVisible] =
    useState<boolean>(false);

  const [entertainerSearch, setEntertainerSearch] = useState<string>();
  const [debouncedEntertainerSearch] = useDebounce(entertainerSearch, 300);
  const { currentCity } = useCurrentUserCity();
  const cityId = cityIdOverride ?? currentCity?._id;
  const { filteredEntertainerIdsAreReady, filteredEntertainerIds, filteredEntertainersTitle } =
    useFilteredEntertainerIdsQuery(cityId, 'dragPerformer', filter);
  const { entertainersAreReady, entertainers } = useHydratedEntertainersQuery(
    cityId,
    'dragPerformer',
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
    },
  );

  const selectedEntertainer = useMemo(() => {
    if (!selectedEntertainerId || !entertainers) {
      return undefined;
    }

    return entertainers[selectedEntertainerId];
  }, [selectedEntertainerId, entertainers]);

  const filteredEntertainers = useMemo(() => {
    const unfilteredEntertainers = Object.values(entertainers ?? {});

    if (filter === EntertainerFilter.All) {
      return unfilteredEntertainers;
    }

    const filterRecord = Object.fromEntries(filteredEntertainerIds.map((id) => [id, true]));

    return unfilteredEntertainers.filter((entertainer) => {
      return filterRecord[entertainer._id];
    });
  }, [filter, entertainers, filteredEntertainerIds]);

  const searchFilteredEntertainers = useMemo(() => {
    const searchValue = debouncedEntertainerSearch?.toLowerCase()?.trim();

    if (!searchValue) {
      return filteredEntertainers;
    }

    return filteredEntertainers.filter((entertainer) => {
      return entertainer.title?.toLowerCase().includes(searchValue);
    });
  }, [filteredEntertainers, debouncedEntertainerSearch]);

  const trendingEntertainers = useMemo(() => {
    return orderBy(
      filteredEntertainers.filter(
        (entertainer) =>
          (entertainer.avatar || entertainer.featuredMedia?.[0]?.image) &&
          (entertainer.trendingScore ?? 0),
      ),
      'trendingScore',
      'desc',
    ).slice(0, 9);
  }, [filteredEntertainers]);

  const currentPath = `/entertainers/support?cityId=${cityId}${filter ? `&filter=${filter}` : ''}${selectedEntertainerId ? `&entertainerId=${selectedEntertainerId}` : ''}`;

  return (
    <Screen
      name="Support Entertainers Screen"
      headerProps={{
        title: <Screen.GagglTitle />,
      }}
      hideToolbar
      disableLocationPermissionGate
    >
      <Screen.Content maxWidth={425}>
        <TitleToolbar text={filteredEntertainersTitle ?? 'Local Performers'} />
        <p className="mt-0">
          <strong>Support Local Drag.</strong> Hype up some performers or just give 'em a like. Just
          a little bit of support goes a long way!
        </p>

        <div>
          <SupportEntertainersProgressCard
            cityId={cityId}
            onSupportClick={() => setIsSupportEntertainersPopupVisible(true)}
          />
        </div>

        {trendingEntertainers.length >= 3 && (
          <>
            <TitleToolbar
              text="Trending performers"
              size="md"
              infoButton={{
                title: 'Trending Performers',
                text: "These performers have recently received comments and likes so they're trending in the area!",
              }}
            />
            <MostSupportedEntertainersGrid
              entertainers={trendingEntertainers}
              onClickEntertainer={(entertainerId) => setSelectedEntertainerId(entertainerId)}
            />

            <TitleToolbar text="All performers" size="md" />
          </>
        )}

        {((filteredEntertainerIdsAreReady && filter !== EntertainerFilter.All) ||
          filter === EntertainerFilter.All) &&
        entertainersAreReady ? (
          <div>
            <Input
              className="mb-2"
              placeholder="Search performers..."
              onChange={(e) => setEntertainerSearch(e.target.value)}
            />

            <div className="d-flex flex-column gap-2">
              {searchFilteredEntertainers?.length ? (
                searchFilteredEntertainers.map((entertainer) => (
                  <div key={entertainer._id}>
                    <SupportEntertainerCard
                      entertainer={entertainer}
                      cityId={cityId}
                      back={currentPath}
                      onCommentClick={() => setSelectedEntertainerId(entertainer._id)}
                    />
                  </div>
                ))
              ) : (
                <EmptyState
                  title="No performers found"
                  text={
                    entertainerSearch
                      ? `No matches for "${entertainerSearch}"`
                      : 'There are no matching entertainers'
                  }
                />
              )}
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </div>
        )}
      </Screen.Content>
      <EntertainerRewiewsPopup
        isVisible={!!selectedEntertainer?._id}
        entertainerId={selectedEntertainer?._id}
        entertainerTitle={selectedEntertainer?.title}
        headerElement={
          <SupportEntertainerCard
            entertainer={selectedEntertainer}
            cityId={cityId}
            back={currentPath}
            onNavigateCallback={() => setSelectedEntertainerId(undefined)}
          />
        }
        onClose={() => setSelectedEntertainerId(undefined)}
      />
      <SupportEntertainersPopup
        isVisible={isSupportEntertainersPopupVisible}
        onClose={() => setIsSupportEntertainersPopupVisible(false)}
        cityId={cityId}
        entertainers={searchFilteredEntertainers}
      />
    </Screen>
  );
});
